import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, Camera, X, Loader2, Sparkles, Share2, Link as LinkIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Produce", "Dairy", "Meat", "Bakery", "Frozen", "Beverages", "Snacks", "Pantry", "Household", "Other"];

function getUserId() {
  return localStorage.getItem("community_user_id") || "anon";
}

export default function ShareReceive() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [form, setForm] = useState({ title: "", description: "", store_name: "", price: "", original_price: "", category: "" });
  const [sharedUrl, setSharedUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Read shared data from service worker cache OR URL query params
  useEffect(() => {
    (async () => {
      try {
        // Try 1: Read from URL query params (GET-based sharing)
        const params = new URLSearchParams(window.location.search);
        const qTitle = params.get("title") || "";
        const qText = params.get("text") || "";
        const qUrl = params.get("url") || "";

        if (qTitle || qText || qUrl) {
          const desc = [qText, qUrl].filter(Boolean).join("\n\n");
          setForm((f) => ({ ...f, title: qTitle, description: desc }));
          if (qUrl) setSharedUrl(qUrl);
          // Try to extract and fetch image from the shared URL/text
          await tryFetchImageFromSharedContent(qText, qUrl);
          setLoading(false);
          return;
        }

        // Try 2: Read from service worker cache (POST-based sharing with files)
        const metaRes = await fetch("/shared-meta");
        if (metaRes.ok) {
          const meta = await metaRes.json();
          const desc = [meta.text, meta.url].filter(Boolean).join("\n\n");
          setForm((f) => ({ ...f, title: meta.title || "", description: desc }));
          if (meta.url) setSharedUrl(meta.url);

          // First check if images were shared directly as files
          let hasImage = false;
          if (meta.imageKeys?.length > 0) {
            const imgRes = await fetch(meta.imageKeys[0]);
            if (imgRes.ok) {
              const blob = await imgRes.blob();
              if (blob.size > 0) {
                setImagePreview(URL.createObjectURL(blob));
                await uploadBlob(blob);
                hasImage = true;
              }
            }
          }

          // If no direct image, try fetching from shared URL/text
          if (!hasImage) {
            await tryFetchImageFromSharedContent(meta.text || "", meta.url || "");
          }
        }
      } catch (e) {
        console.error("Failed to read shared data:", e);
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  // Extract image URLs from shared text/URL and fetch via backend proxy
  const tryFetchImageFromSharedContent = async (text, url) => {
    const allText = `${text} ${url}`;

    // Try multiple patterns to find image URLs
    const patterns = [
      // Direct image file URLs
      /https?:\/\/[^\s"'<>]+\.(jpg|jpeg|png|gif|webp)([?&][^\s"'<>]*)?/gi,
      // Facebook CDN images (fbcdn, scontent)
      /https?:\/\/[^\s"'<>]*(?:fbcdn|scontent)[^\s"'<>]+/gi,
      // Instagram images
      /https?:\/\/[^\s"'<>]*instagram[^\s"'<>]*\.(?:jpg|jpeg|png)[^\s"'<>]*/gi,
      // Image hosting services (picsum, imgur, etc.)
      /https?:\/\/(?:picsum\.photos|i\.imgur\.com|images\.unsplash\.com)[^\s"'<>]+/gi,
    ];

    let targetUrl = null;
    for (const pattern of patterns) {
      const match = allText.match(pattern);
      if (match) {
        targetUrl = match[0];
        break;
      }
    }

    // If no direct image URL found, try the shared URL itself
    // (the backend proxy will try og:image extraction from the page)
    if (!targetUrl && url && !url.includes("foodzlizts") && !url.includes("grocery-ads")) {
      targetUrl = url;
    }

    // Last resort: try any URL found in the text
    if (!targetUrl) {
      const anyUrl = allText.match(/https?:\/\/[^\s"'<>]+/i);
      if (anyUrl && !anyUrl[0].includes("foodzlizts") && !anyUrl[0].includes("grocery-ads")) {
        targetUrl = anyUrl[0];
      }
    }

    if (targetUrl) {
      try {
        setUploading(true);
        const res = await fetch(`${BACKEND_URL}/api/community/fetch-image`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: targetUrl }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.url) {
            setImageUrl(data.url);
            setImagePreview(data.url);
          }
        }
      } catch (e) {
        console.error("Image fetch from URL failed:", e);
      }
      setUploading(false);
    }
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "shared-image.jpg");
      const res = await fetch(`${BACKEND_URL}/api/community/upload`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.url || "");
      }
    } catch (e) {
      console.error("Upload failed:", e);
    }
    setUploading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    await uploadBlob(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/community/deals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: getUserId(),
          title: form.title || form.description.slice(0, 60),
          description: form.description,
          store_name: form.store_name,
          price: form.price ? parseFloat(form.price) : null,
          original_price: form.original_price ? parseFloat(form.original_price) : null,
          category: form.category,
          image_url: imageUrl,
          source_url: sharedUrl || undefined,
        }),
      });
      if (res.ok) {
        // Clean up share cache
        if ("caches" in window) {
          const cache = await caches.open("foodz-lizts-share");
          const keys = await cache.keys();
          await Promise.all(keys.map((k) => cache.delete(k)));
        }
        toast({ title: "Deal posted!", description: "Your shared deal is now live in Community Deals." });
        navigate("/deals");
      } else {
        toast({ title: "Error", description: "Failed to post deal. Try again.", variant: "destructive" });
      }
    } catch (e) {
      console.error("Post failed:", e);
      toast({ title: "Error", description: "Something went wrong.", variant: "destructive" });
    }
    setPosting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="share-receive-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-violet-500 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading shared content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="share-receive-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/")} className="text-gray-600 hover:text-gray-900" data-testid="share-receive-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Share2 className="h-5 w-5 text-violet-600" />
        <h1 className="font-bold text-gray-900">Post Shared Deal</h1>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-md mx-auto">
        {/* Source URL indicator */}
        {sharedUrl && (
          <Card className="border-violet-200 bg-violet-50">
            <CardContent className="p-3 flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-violet-500 flex-shrink-0" />
              <p className="text-xs text-violet-700 truncate">{sharedUrl}</p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Photo upload */}
          <div
            onClick={() => fileRef.current?.click()}
            className={`relative border-2 border-dashed rounded-xl cursor-pointer transition-colors text-center ${
              imagePreview ? "border-violet-300 bg-violet-50" : "border-gray-200 hover:border-violet-300 hover:bg-violet-50/30"
            }`}
            data-testid="share-photo-upload"
          >
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-contain rounded-lg" />
                {uploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                    <Loader2 className="h-6 w-6 text-violet-500 animate-spin" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setImagePreview(null); setImageUrl(""); }}
                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="py-8">
                <Camera className="h-8 w-8 text-violet-400 mx-auto mb-2" />
                <p className="text-sm text-violet-600 font-medium">Add or change photo</p>
                <p className="text-xs text-gray-400">Tap to take a photo or choose from gallery</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Deal Title</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Buy 1 Get 1 Free Strawberries" className="text-sm" data-testid="share-deal-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe the deal..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 min-h-[80px]"
              data-testid="share-deal-description"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Store Name</label>
            <Input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} placeholder="e.g. Kroger, Target, ALDI..." className="text-sm" data-testid="share-deal-store" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Sale Price ($)</label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="2.99" className="text-sm" data-testid="share-deal-price" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Original Price ($)</label>
              <Input type="number" step="0.01" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="4.99" className="text-sm" data-testid="share-deal-original-price" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat} type="button"
                  onClick={() => setForm({ ...form, category: form.category === cat ? "" : cat })}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${form.category === cat ? "bg-violet-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <Button
            type="submit"
            disabled={posting || !form.description.trim() || uploading}
            className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
            data-testid="share-submit-deal"
          >
            {posting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Posting...</> : <><Sparkles className="h-4 w-4 mr-2" />Post to Community Deals</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
